import { Box, BoxProps, ThemeUIStyleObject } from 'theme-ui';
import React from 'react';

import * as _icons from './icons';

export type IconName = keyof typeof _icons;

export interface IconProps extends React.SVGProps<SVGSVGElement> {
  icon: IconName;
  circular?: boolean;
  circleProps?: BoxProps;
  preserveColors?: boolean;
  sx?: ThemeUIStyleObject;
}

// Export list of icon names as an array to use in Storybook or such
export const icons = Object.keys(_icons) as IconName[];

const Icon: React.FC<IconProps> = ({ circular, circleProps, icon, preserveColors, sx, ...props }) => {
  const IconComponent: React.ReactNode = _icons[icon];

  if (typeof IconComponent !== 'function') {
    return null;
  }

  // NOTE: sx as a prop takes another (more flexible) SxStyleProp type
  // But we use a fixed style object here
  const defaultSx: ThemeUIStyleObject = preserveColors
    ? // Unless the icon should specifically preserve its original colors
      {}
    : // We will cascade fill: currentColor to all elements
      // https://css-tricks.com/cascading-svg-fill-color/
      {
        '*': {
          fill: 'currentColor'
        }
      };

  return circular ? (
    <Box as="span" variant="circles.default" {...circleProps}>
      <IconComponent
        aria-label={icon}
        sx={{
          ...defaultSx,
          ...sx
        }}
        {...props}
      />
    </Box>
  ) : (
    <IconComponent
      aria-label={icon}
      sx={{
        ...defaultSx,
        ...sx
      }}
      {...props}
    />
  );
};

Icon.defaultProps = {
  role: 'img',
  width: 24,
  height: 24,
  circular: false,
  circleProps: {
    sx: {
      width: 40,
      height: 40
    }
  },
  preserveColors: false
};

export default Icon;
